import { Capacitor } from '@capacitor/core';

/**
 * Microservices API Configuration
 * 
 * This configuration handles routing for both web and Capacitor (mobile) platforms
 * to support the new microservices architecture while maintaining backward compatibility.
 */

// ============================================
// BASE URL CONFIGURATION
// ============================================

export const getApiBaseUrl = () => {
  const isCapacitor = Capacitor.isNativePlatform();
  
  if (isCapacitor) {
    // In Capacitor native apps, use the full backend URL from capacitor.config.json hostname
    // or fallback to environment variable
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // In web browser, use the full backend URL from environment
  return process.env.REACT_APP_BACKEND_URL || '';
};

export const API_BASE_URL = getApiBaseUrl();

// ============================================
// SERVICE ENDPOINTS
// ============================================

/**
 * Microservices Architecture:
 * - API Gateway routes requests to appropriate microservices
 * - All requests go through /api/* prefix
 * - Gateway handles service discovery and load balancing
 */

export const SERVICES = {
  // API Gateway (routes to monolith or microservices)
  GATEWAY: `${API_BASE_URL}/api`,
  
  // Location Service (standalone microservice)
  // Gateway routes /api/location/* to Location Service
  LOCATION: `${API_BASE_URL}/api/location`,
  
  // Ride Service (monolith for now, will be extracted)
  RIDES: `${API_BASE_URL}/api/rides`,
  
  // Auth Service (monolith)
  AUTH: `${API_BASE_URL}/api/auth`,
  
  // User Service (monolith)
  USERS: `${API_BASE_URL}/api`,
  
  // Admin Service (monolith)
  ADMIN: `${API_BASE_URL}/api/admin`,
  
  // Verification Service (monolith)
  VERIFICATION: `${API_BASE_URL}/api/verification`,
  
  // Payment Service (monolith)
  PAYMENTS: `${API_BASE_URL}/api/payments`,
  
  // Notification Service (monolith)
  NOTIFICATIONS: `${API_BASE_URL}/api/notifications`,
  
  // BFF Routes (Backend for Frontend)
  BFF_RIDER: `${API_BASE_URL}/api/bff/rider`,
  BFF_DRIVER: `${API_BASE_URL}/api/bff/driver`,
};

// Legacy compatibility (for old code)
export const API_URL = SERVICES.GATEWAY;

// ============================================
// CAPACITOR-AWARE HELPERS
// ============================================

/**
 * Get WebSocket URL for Socket.IO connections
 * Handles Capacitor native platform vs web browser
 */
export const getSocketUrl = () => {
  const isCapacitor = Capacitor.isNativePlatform();
  
  if (isCapacitor) {
    // On native platform, use the full backend URL
    // Socket.IO needs explicit URL on mobile
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // On web, use relative or environment URL
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

// Socket.IO path — must match the server's socketio_path. Our server mounts
// it under /api/ so the Kubernetes ingress routes it to the backend.
export const SOCKET_PATH = '/api/socket.io';

/**
 * Get full URL for images/assets stored on backend
 * Handles relative paths from database
 */
export const getAssetUrl = (path) => {
  if (!path) return '';
  
  // If already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Construct full URL for relative paths
  const baseUrl = process.env.REACT_APP_BACKEND_URL || API_BASE_URL;
  return `${baseUrl}${path}`;
};

// ============================================
// FEATURE FLAGS
// ============================================

/**
 * Feature flags to control microservices usage
 * Enable gradually as services are deployed
 */
export const FEATURE_FLAGS = {
  // Use standalone Location Service (Phase 2 complete)
  USE_LOCATION_MICROSERVICE: true,
  
  // Use standalone Ride Service (Phase 3 - not yet)
  USE_RIDE_MICROSERVICE: false,
  
  // Use standalone Dispatch Service (Phase 4 - not yet)
  USE_DISPATCH_MICROSERVICE: false,
  
  // Use standalone Pricing Service (Phase 5 - not yet)
  USE_PRICING_MICROSERVICE: false,
};

// ============================================
// SERVICE-SPECIFIC ENDPOINTS
// ============================================

/**
 * Location Service Endpoints
 * These can route to either monolith or microservice based on feature flag
 */
export const LOCATION_ENDPOINTS = {
  UPDATE: FEATURE_FLAGS.USE_LOCATION_MICROSERVICE 
    ? `${API_BASE_URL}/api/location/update`
    : `${API_BASE_URL}/api/location/update`,
  
  NEARBY: FEATURE_FLAGS.USE_LOCATION_MICROSERVICE
    ? `${API_BASE_URL}/api/location/nearby`
    : `${API_BASE_URL}/api/location/nearby`,
  
  GET_DRIVER: (driverId) => FEATURE_FLAGS.USE_LOCATION_MICROSERVICE
    ? `${API_BASE_URL}/api/location/driver/${driverId}`
    : `${API_BASE_URL}/api/location/driver/${driverId}`,
  
  DRIVER_ONLINE: (driverId) => `${API_BASE_URL}/api/location/driver/${driverId}/online`,
  DRIVER_OFFLINE: (driverId) => `${API_BASE_URL}/api/location/driver/${driverId}/offline`,
  
  TRACKING_START: `${API_BASE_URL}/api/location/tracking/start`,
  TRACKING_STOP: (driverId) => `${API_BASE_URL}/api/location/tracking/stop/${driverId}`,
  
  STATS: `${API_BASE_URL}/api/location/stats`,
};

/**
 * Ride Service Endpoints
 */
export const RIDE_ENDPOINTS = {
  CREATE: `${SERVICES.RIDES}/create`,
  GET_RIDER_RIDES: (riderId, limit = 50) => `${SERVICES.RIDES}/rider/${riderId}?limit=${limit}`,
  GET_DRIVER_RIDES: (driverId, limit = 50) => `${SERVICES.RIDES}/driver/${driverId}?limit=${limit}`,
  GET_RIDE: (rideId) => `${SERVICES.RIDES}/${rideId}`,
  ACCEPT: (rideId) => `${SERVICES.RIDES}/${rideId}/accept`,
  START: (rideId) => `${SERVICES.RIDES}/${rideId}/start`,
  COMPLETE: (rideId) => `${SERVICES.RIDES}/${rideId}/complete`,
  CANCEL: (rideId) => `${SERVICES.RIDES}/${rideId}/cancel`,
  RATE: (rideId) => `${SERVICES.RIDES}/${rideId}/rate`,
};

/**
 * Auth Service Endpoints
 */
export const AUTH_ENDPOINTS = {
  REGISTER_RIDER: `${SERVICES.AUTH}/register/rider`,
  REGISTER_DRIVER: `${SERVICES.AUTH}/register/driver`,
  LOGIN: `${SERVICES.AUTH}/login`,
  VERIFY_OTP: `${SERVICES.AUTH}/verify-otp`,
  RESEND_OTP: `${SERVICES.AUTH}/resend-otp`,
};

/**
 * User Service Endpoints
 */
export const USER_ENDPOINTS = {
  GET_USER: (userId) => `${SERVICES.USERS}/users/${userId}`,
  GET_BY_PHONE: (phone, countryCode) => `${SERVICES.USERS}/users/phone/${phone}?country_code=${countryCode}`,
  UPDATE_PROFILE: `${SERVICES.USERS}/user/profile`,
  GET_PROFILE: `${SERVICES.USERS}/user/profile`,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the appropriate endpoint based on feature flags
 */
export const getEndpoint = (service, path) => {
  return `${SERVICES[service]}${path}`;
};

/**
 * Check if running in Capacitor (mobile app)
 */
export const isCapacitor = () => Capacitor.isNativePlatform();

/**
 * Get current platform
 */
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Get service health status URL
 */
export const getHealthCheckUrl = (service) => {
  switch(service) {
    case 'location':
      return `${API_BASE_URL}/api/location/health`;
    case 'gateway':
      return `${API_BASE_URL}/api/health`;
    default:
      return `${API_BASE_URL}/api/health`;
  }
};

// ============================================
// LOGGING
// ============================================

console.log('🚀 Microservices API Configuration:', {
  isCapacitor: isCapacitor(),
  platform: getPlatform(),
  apiBaseUrl: API_BASE_URL,
  services: Object.keys(SERVICES),
  featureFlags: FEATURE_FLAGS,
  environment: process.env.NODE_ENV,
});

// Export everything
export default {
  API_BASE_URL,
  API_URL,
  SERVICES,
  FEATURE_FLAGS,
  LOCATION_ENDPOINTS,
  RIDE_ENDPOINTS,
  AUTH_ENDPOINTS,
  USER_ENDPOINTS,
  getApiBaseUrl,
  getEndpoint,
  isCapacitor,
  getPlatform,
  getHealthCheckUrl,
};
